import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "./MercadoShopsTable.css";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import {
  ButtonCommon,
  CustomButton,
  ColumnSelectFitType,
  ColumnSelectSizeGuide,
  Tooltip,
  ModalIntroMs,
} from "../../common";
import { FormattedMessage, useIntl } from "react-intl";
import {
  ProductServices,
  StorageServices,
  UserServices,
} from "../../../services";
import PlatformsService from "../../../services/platform-services/PlatformService";

interface PlatformProduct {
  CompanyId: number;
  Id?: number;
  Name: string;
  SizeGuide: any;
  Sizes: string[];
  Status: any;
  FitType: any;
  ImageUrl: string;
  Classified: boolean;
  PlatformCode: string;
  PlatformProductId: number;
  Modified: boolean;
}

export default function MercadoShopsTable() {
  const synchMarketplace =
    StorageServices.GetCookieUser().ShowOnBoardMarketplace;

  const [filters, setFilters] = useState<any>({
    global: { value: null, matchMode: "contains" },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState<PlatformProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<PlatformProduct[]>(
    []
  );
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [noClasificadoCount, setNoClasificadoCount] = useState<number>(0);
  const [deshabilitadoCount, setDeshabilitadoCount] = useState<number>(0);
  const [modificadosCount, setModificadosCount] = useState<number>(0);
  const [filterNoClasificado, setFilterNoClasificado] =
    useState<boolean>(false);
  const [filterDeshabilitado, setFilterDeshabilitado] =
    useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(
    StorageServices.GetCookieUser().ShowOnBoardMarketplace || false
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("readysizelocale") || "en"
  );

  async function getProductos() {
    try {
      setIsLoading(true);

      const getProductResponse = await ProductServices.GetPlatformProducts();

      if (getProductResponse && getProductResponse.Status === 200) {
        setProducts(getProductResponse.Content);

        calculateStatusCounts(getProductResponse.Content);
      } else {
        setProducts([]);
        calculateStatusCounts([]);
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);

      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    if (!synchMarketplace) getProductos();
  }, [isModalOpen, products.length]);

  const calculateStatusCounts = (data: PlatformProduct[]) => {
    const noClasificadoItems = data.filter((item) => !item.Classified);

    setNoClasificadoCount(noClasificadoItems.length);

    const deshabilitadoItems = data.filter((item) => item.Status.Id !== 1);

    setDeshabilitadoCount(deshabilitadoItems.length);

    const modificadosItems = data.filter((item) => item.Modified);

    setModificadosCount(modificadosItems.length);
  };

  const imageBodyTemplate = (product: PlatformProduct) => {
    const defaultImageUrl = "img/readysizelogo.png";
    const imageUrl = product.ImageUrl || defaultImageUrl;

    return (
      <img
        src={imageUrl}
        alt={product.PlatformCode}
        className="mercadoshops-image"
      />
    );
  };

  const SizeGuideBodyTemplate = (product: PlatformProduct) => {
    return (
      <ColumnSelectSizeGuide
        value={product.SizeGuide.Id.toString()}
        placeholder="Elegir Guía"
        ByCode={true}
        onChange={(e: any) => handleSizeGuideChange(product, e)}
      />
    );
  };

  const FitBodyTemplate = (product: PlatformProduct) => {
    return (
      <ColumnSelectFitType
        value={product.FitType.Id.toString()}
        placeholder="Elegir Fit"
        onChange={(e: any) => handleFitTypeChange(product, e)}
      />
    );
  };

  const handleStatusChange = (product: PlatformProduct, newValue: boolean) => {
    const newCondicion = newValue;
    const updatedProducts = products.map((p) =>
      p.PlatformProductId === product.PlatformProductId
        ? {
            ...p,
            Status: {
              Id: newCondicion ? 1 : 2,
              Name: newCondicion ? "HABILITADO" : "DESHABILITADO",
            },
            Modified: true,
          }
        : p
    );

    setProducts(updatedProducts);
    calculateStatusCounts(updatedProducts);
  };

  const handleSizeGuideChange = (product: PlatformProduct, newValue: any) => {
    const updatedProducts = products.map((p) =>
      p.PlatformProductId === product.PlatformProductId
        ? {
            ...p,
            SizeGuide: { Id: newValue, Name: "" },
            Classified: true,
            Status: { Id: 1, Name: "HABILITADO" },
            Modified: true,
          }
        : p
    );

    setProducts(updatedProducts);
    calculateStatusCounts(updatedProducts);
  };

  const handleFitTypeChange = (product: PlatformProduct, newValue: any) => {
    const updatedProducts = products.map((p) =>
      p.PlatformProductId === product.PlatformProductId
        ? { ...p, FitType: { Id: newValue, Name: "" }, Modified: true }
        : p
    );

    setProducts(updatedProducts);
    calculateStatusCounts(updatedProducts);
  };

  const SizesBodyTemplate = (product: PlatformProduct) => {
    return product.Sizes?.join(", "); // Join the talle array with commas
  };

  const StatusBodyTemplate = (product: PlatformProduct) => {
    let condicionStyle = "condicion-badge";
    let condicionText = "";

    if (product.Status.Id === 1) {
      condicionStyle += " habilitado";
      condicionText = ""; //"Habilitado";
    } else {
      condicionStyle += " condicion-deshabilitado";
      condicionText = ""; // "Deshabilitado";
    }

    condicionStyle = "";

    return (
      <div className="estado-switch-container">
        <span className={condicionStyle}>{condicionText}</span>
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="estado-switch-main-container"
        >
          <InputSwitch
            checked={product.Classified && product.Status.Id === 1}
            onChange={(e) => handleStatusChange(product, e.value)}
          />
        </div>
      </div>
    );
  };

  //const ratingBodyTemplate = (product: Product) => {
  //  return <Rating value={product.rating} readOnly cancel={false} />;
  //};

  const ClassifiedBodyTemplate = (product: PlatformProduct) => {
    let statusStyle = "estado-badge";
    let statusText: React.ReactNode = ""; // Explicitly type statusText as React.ReactNode

    if (product.Classified) {
      statusText = (
        <FormattedMessage
          id="tooltip.paragraph3"
          defaultMessage="Clasificado"
        />
      );
      statusStyle += " estado-clasificado"; // Add estado-clasificado class if product is classified
    } else {
      // Display an empty string instead of "No Clasificado"
      statusText = "";
    }

    return (
      <span className={statusStyle}>
        <b>{statusText}</b>
      </span>
    );
  };

  const RefreshBodyTemplate = (product: PlatformProduct) => {
    return (
      <>
        <Button
          icon="pi pi-refresh"
          onClick={() => onClickRefreshItem(product)}
          className="ms-refresh-button"
        />
      </>
    );
  };

  const handleNoClasificadoClick = () => {
    try {
      let _filters = { ...filters };
      let value: any = null;

      if (!filterNoClasificado) value = false;

      if (value === null && filterDeshabilitado) value = "Deshabilitado";

      _filters["global"].value = value;

      setFilterNoClasificado(!filterNoClasificado);
      setFilters(filters);
    } catch (e) {
      console.log("ERROR handleNoClasificadoClick", e);
    }
  };

  const handleDeshabilitadoClick = () => {
    try {
      let _filters = { ...filters };
      let value: any = null;

      if (!filterDeshabilitado) value = "Deshabilitado";

      if (value === null && filterNoClasificado) value = false;

      _filters["global"].value = value;

      setFilterDeshabilitado(!filterDeshabilitado);
      setFilters(filters);
    } catch (e) {
      console.log("ERROR handleDeshabilitadoClick", e);
    }
  };

  const onClickSave = async () => {
    try {
      const updateProducts = products.filter((p) => {
        return p.Modified || false;
      });

      if (updateProducts && updateProducts.length > 0) {
        setIsLoading(true);

        const getProductResponse = await ProductServices.PostPlatformProducts(
          updateProducts
        );

        if (getProductResponse && getProductResponse.Status === 200) {
          if (getProductResponse.Content) {
            await getProductos();
          }
        }

        setIsLoading(false);
      }

      //console.log('updateProducts Save', updateProducts);
    } catch (e) {
      console.log("ERROR SAVE", e);

      setIsLoading(false);
    }
  };

  const onClickRefresh = async () => {
    try {
      await getProductos();
    } catch (error) {
      console.log(error);
    }
  };

  const onClickRefreshItem = async (product: PlatformProduct) => {
    try {
      setIsLoading(true);

      const getMarketplaceResponse =
        await PlatformsService.GetMercadoShopsItemDetail(product.PlatformCode);

      if (getMarketplaceResponse && getMarketplaceResponse.Status === 200) {
        const getProductResponse = await ProductServices.GetPlatformProduct(
          product.PlatformCode
        );

        if (getProductResponse && getProductResponse.Status === 200) {
          const productPlatform: PlatformProduct =
            getProductResponse.Content[0];

          const updatedProducts = products.map((p) =>
            p.PlatformProductId === product.PlatformProductId
              ? {
                  ...p,
                  Name: productPlatform.Name,
                  ImageUrl: productPlatform.ImageUrl,
                  Sizes: productPlatform.Sizes,
                }
              : p
          );

          setProducts(updatedProducts);
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);

      setIsLoading(false);
    }
  };

  const onClickSynchMarketPlace = async () => {
    try {
      setIsLoading(true);

      const getMarketplaceResponse =
        await PlatformsService.GetMercadoShopsSearchItems(true);

      if (getMarketplaceResponse && getMarketplaceResponse.Status === 200) {
        await getProductos();
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);

      setIsLoading(false);
    }
  };

  const intl = useIntl();

  const header = (
    <div className="mercadoshops-search-container">
      <div className="mercadoshops-products-alerts">
        {/*<span className="text-xl text-900 font-bold"></span>*/}
        {/* <CustomButton
          buttonText="No Clasificado"
          quantity={noClasificadoCount}
          onClick={handleNoClasificadoClick}
        />
        <CustomButton
          buttonText="Deshabilitado"
          quantity={deshabilitadoCount}
          onClick={handleDeshabilitadoClick}
        /> */}
        <CustomButton
          messageId="ready-size-ms-noclasificado"
          quantity={noClasificadoCount}
          onClick={handleNoClasificadoClick}
        />
        <CustomButton
          messageId="ready-size-deshabilitado"
          quantity={deshabilitadoCount}
          onClick={handleDeshabilitadoClick}
        />
      </div>

      <div
        className="ms-search-refresh-container-table"
        style={{ width: (modificadosCount || 0) === 0 ? "645px" : "690px" }}
      >
        {/*                   
                 <Tooltip />
              */}
        <input
          type="text"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={intl.formatMessage({
            id: "ms-table-column-search",
            defaultMessage: "Buscar producto...",
          })}
          className="p-inputtext p-component"
        />
        {/*<ButtonCommon*/}
        {/*    label={*/}
        {/*        <FormattedMessage*/}
        {/*            id="ready-size-marketplace-actualizar"*/}
        {/*            defaultMessage="Synch Mercado Shops"*/}
        {/*        />*/}
        {/*    }*/}
        {/*    isOutline={true}*/}
        {/*    eventClick={() => onClickSynchMarketPlace()}*/}
        {/*/>*/}

        <div className="">
          <ButtonCommon
            label={
              <FormattedMessage
                id="ready-size-ms-load"
                defaultMessage="Cargar"
              />
            }
            isOutline={false}
            eventClick={() => onClickRefresh()}
          />
          {/* <Button
            icon="pi pi-refresh"
            onClick={() => onClickRefresh()}
            // rounded
            // raised
            className="ms-refresh-button"
          /> */}
        </div>
        {/* <Button
          // icon="pi pi-refresh"
          label="Guardar"
          // rounded
          // raised
          className="ms-save-button"
        /> */}
        {/*<ButtonCommon*/}
        {/*  label={*/}
        {/*    <FormattedMessage id="ready-size-sync" defaultMessage="Sync MS" />*/}
        {/*  }*/}
        {/*  isOutline={false}*/}
        {/*  eventClick={() => onClickSave()}*/}
        {/*/>*/}
        <ButtonCommon
          label={
            <FormattedMessage
              id="ready-size-marketplace-actualizar"
              defaultMessage="Sync MS"
            />
          }
          isOutline={true}
          eventClick={() => onClickSynchMarketPlace()}
          Styles={{ width: (modificadosCount || 0) === 0 ? "220px" : "320px" }}
        />
        <ButtonCommon
          label={
            <FormattedMessage
              id="ready-size-Guardar"
              defaultMessage="Guardar"
            />
          }
          isOutline={false}
          eventClick={() => onClickSave()}
          Badge={modificadosCount}
          Styles={{ width: (modificadosCount || 0) === 0 ? "100px" : "210px" }}
        />
      </div>
    </div>
  );

  const headerCondition = () => {
    return (
      <div className="rs-condicion-container">
        <p className="rs-condicion-container-p">
          <FormattedMessage
            id="ms-table-column-condition"
            defaultMessage="Condición"
          />
        </p>
        {/*                    
        <Button
          icon="pi pi-question-circle"
          className="readysize-tooltip-container"
        />
        */}
        <Tooltip
          headingId="tooltip.heading2"
          paragraphIds={["tooltip.paragraph3", "tooltip.paragraph4"]}
          firstParagraphBackgroundColor="#8e5cf1" // Set the background color for the first paragraph
        />
      </div>
    );
  };

  const headerEnabled = () => {
    return (
      <div className="rs-condicion-container">
        <p className="rs-condicion-container-p">
          <FormattedMessage
            id="ms-table-column-enabled"
            defaultMessage="Habilitado"
          />
        </p>
        {/*                    
        <Button
          icon="pi pi-question-circle"
          className="readysize-tooltip-container"
        />
        */}
        <Tooltip
          headingId="tooltip.heading"
          paragraphIds={["tooltip.paragraph1", "tooltip.paragraph2"]}
          firstParagraphBackgroundColor="#20C997"
        />
      </div>
    );
  };

  const footer = (
    <>
      <FormattedMessage id="ms-footer-first" defaultMessage="En total hay" />{" "}
      {products ? products.length : 0}{" "}
      <FormattedMessage id="ms-footer-second" defaultMessage="productos" />.
    </>
  );

  // const footer = `En total hay ${products ? products.length : 0} productos.`;

  const onPageChange = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const onCloseModalOnBoarding = async () => {
    try {
      setIsModalOpen(false);
      setIsLoading(true);

      try {
        const postStatusOnBoarding = await UserServices.UpdateStatusOnBoarding(
          2
        );

        if (postStatusOnBoarding && postStatusOnBoarding.Status === 200) {
          // Get the current user
          let user = StorageServices.GetCookieUser();

          // Update the ShowOnBoardMarketplace property
          user.ShowOnBoardMarketplace = false;

          // Save the updated user back to the cookie
          StorageServices.SetCookieUser(user);
        }
      } catch (eOnBoarding) {
        console.log("ERROR Update OnBoarding", eOnBoarding);
      }

      const getMarketplaceResponse =
        await PlatformsService.GetMercadoShopsSearchItems(true);

      if (getMarketplaceResponse && getMarketplaceResponse.Status === 200) {
        await getProductos();
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);

      setIsLoading(false);
    }
  };

  //const statusField = "inventoryStatus";

  return (
    <div className="card">
      {isModalOpen && (
        <ModalIntroMs
          closeModal={() => onCloseModalOnBoarding()}
          selectedLanguage={selectedLanguage as "en" | "es" | "pt"} // Ensure selectedLanguage is of type "en" | "es" | "pt"
          user={StorageServices.GetCookieUser()}
        />
      )}
      <DataTable
        value={products}
        header={header}
        footer={footer}
        first={first}
        rows={rows}
        paginator
        onPage={onPageChange}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        rowsPerPageOptions={[5, 10, 25, 50]}
        globalFilter={globalFilter}
        filters={filters}
        globalFilterFields={[
          "PlatformCode",
          "Name",
          "SizeGuide.Name",
          "FitType.Name",
          "Classified",
          "Status.Name",
        ]}
        emptyMessage={
          <FormattedMessage
            id="ms-table-column-emptymessage"
            defaultMessage="No se encontraron productos."
          />
        }
        selectionMode="multiple"
        selection={selectedProducts}
        onSelectionChange={(e) => setSelectedProducts(e.value)}
        loading={isLoading}
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: "3rem" }}
        ></Column>
        <Column
          header={
            <FormattedMessage
              id="ms-table-column-image"
              defaultMessage="Imagen"
            />
          }
          body={imageBodyTemplate}
        ></Column>
        <Column
          field="PlatformCode"
          header={
            <FormattedMessage
              id="ms-table-column-code"
              defaultMessage="Código"
            />
          }
          sortable
        ></Column>
        <Column
          field="Name"
          header={
            <FormattedMessage
              id="ms-table-column-name"
              defaultMessage="Nombre"
            />
          }
          sortable
        ></Column>
        <Column
          // field="SizeGuide"
          header={
            <FormattedMessage
              id="ms-table-column-sizeguide"
              defaultMessage="Guía de talles"
            />
          }
          body={SizeGuideBodyTemplate}
        ></Column>
        <Column
          header={
            <FormattedMessage
              id="ms-table-column-sizes"
              defaultMessage="Talles"
            />
          }
          body={SizesBodyTemplate}
        ></Column>
        <Column
          // field="FitType"
          // sortable
          header={
            <FormattedMessage id="ms-table-column-fit" defaultMessage="Calce" />
          }
          body={FitBodyTemplate}
        ></Column>
        <Column
          header={headerCondition}
          body={ClassifiedBodyTemplate}
          sortable
          // field="Classified"
        ></Column>
        <Column
          header={headerEnabled}
          body={StatusBodyTemplate}
          sortable
          // field="Status"
          className="ms-table-estado-row"
        ></Column>
        <Column header="" body={RefreshBodyTemplate}></Column>
      </DataTable>
    </div>
  );
}
